/**
 * One window for everything that goes into a staff file: a leave request,
 * a performance review, a disciplinary action, a letter, a document or a
 * note. Each kind asks only for what it needs, in plain words.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useHrAddRecord } from "../../api/queries";
import { Button, Field, Input, Select } from "../../design/components";
import type { HrEmployee } from "../../domain/hr";
import { REQUEST_TYPES } from "../../domain/hrClock";
import { LEAVE_BY, LEAVE_TYPES, LETTER_TYPES, type RecordKind } from "../../domain/hrFile";
import { t } from "../../i18n";
import { useHrAccess } from "./access";
import { addDays, todayIso } from "./kit";
import { Err, s } from "./shared";

export const RATINGS = [
  "Outstanding",
  "Exceeds expectations",
  "Meets expectations",
  "Needs improvement",
  "Unsatisfactory",
];
export const ACTIONS = [
  "Verbal warning",
  "Written warning",
  "Final written warning",
  "Salary deduction",
  "Suspension without pay",
  "Dismissal (art. 80)",
];

const KIND_TITLE: Record<RecordKind, string> = {
  leave: "Request leave",
  review: "Add a performance review",
  discipline: "Record a disciplinary action",
  letter: "Request or issue a letter",
  document: "Add a document",
  note: "Add a note",
  request: "Log a request for this person",
};

export function RecordDialog({
  employee,
  kind,
  onClose,
}: {
  employee: HrEmployee;
  kind: RecordKind;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open && typeof d.showModal === "function") d.showModal();
  }, []);
  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} data-testid="hr-record-dialog">
      <RecordForm employee={employee} kind={kind} onClose={onClose} />
    </dialog>
  );
}

function RecordForm({ employee, kind, onClose }: { employee: HrEmployee; kind: RecordKind; onClose: () => void }) {
  const { can } = useHrAccess();
  const add = useHrAddRecord();
  const today = todayIso();
  const [f, setF] = useState({
    type: kind === "request" ? "status_change" : "annual",
    from: addDays(today, 7),
    to: addDays(today, 13),
    title: "",
    rating: RATINGS[2],
    strengths: "",
    goals: "",
    action: ACTIONS[0],
    article: "",
    letter: "employment",
    addressee: "",
    purpose: "",
    text: "",
    now: false,
    file: "" as string,
    fileName: "",
    on: today,
  });
  const [tried, setTried] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const days =
    f.from && f.to && f.to >= f.from ? Math.round((Date.parse(f.to) - Date.parse(f.from)) / 86_400_000) + 1 : 0;
  const problems: Record<string, string> = {};
  if (kind === "leave" && (!f.from || !f.to || f.to < f.from))
    problems.dates = t("The last day cannot be before the first.");
  if (
    (kind === "discipline" || kind === "document" || kind === "note" || kind === "request") &&
    f.title.trim().length < 2
  )
    problems.title = t("Say in a few words what this is.");
  const letterDef = LETTER_TYPES.find((l) => l.key === f.letter);
  if (kind === "letter" && letterDef?.needsPay && !can.pay)
    problems.letter = t("This letter shows pay; it needs the pay permission.");
  if (f.file.length > 400_000) problems.file = t("The file is too large; 300 KB at most.");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.keys(problems).length) return;
    const base = { employeeId: employee.id, kind };
    let input;
    switch (kind) {
      case "leave":
        input = {
          ...base,
          title: LEAVE_BY.get(f.type)?.label ?? "Leave",
          startsOn: f.from,
          endsOn: f.to,
          status: f.now && can.approve ? ("approved" as const) : ("requested" as const),
          data: { type: f.type, days, note: f.text || undefined },
        };
        break;
      case "review":
        input = {
          ...base,
          title: f.title.trim() || `Performance review ${new Date().getUTCFullYear()}`,
          occurredOn: f.on,
          data: { rating: f.rating, score: 5 - RATINGS.indexOf(f.rating), strengths: f.strengths, goals: f.goals },
        };
        break;
      case "discipline":
        input = {
          ...base,
          title: f.title.trim(),
          occurredOn: f.on,
          data: { action: f.action, article: f.article || undefined, note: f.text || undefined },
        };
        break;
      case "letter":
        input = {
          ...base,
          title: letterDef?.label ?? "Letter",
          status: f.now && can.approve ? ("issued" as const) : ("requested" as const),
          data: { letter_type: f.letter, addressee: f.addressee || undefined, purpose: f.purpose || undefined },
        };
        break;
      case "document":
        input = {
          ...base,
          title: f.title.trim(),
          occurredOn: f.on,
          data: { file: f.fileName || undefined, file_data: f.file || undefined, note: f.text || undefined },
        };
        break;
      case "request":
        input = {
          ...base,
          title: f.title.trim(),
          status: "requested" as const,
          data: { type: f.type, details: f.text || undefined },
        };
        break;
      default:
        input = { ...base, title: f.title.trim(), data: { text: f.text || undefined } };
    }
    add.mutate(input, { onSuccess: onClose });
  };

  const show = (k: string) => (tried ? problems[k] : undefined);
  const leaveDef = LEAVE_BY.get(f.type);

  return (
    <form onSubmit={submit} className={s.dialogForm} noValidate>
      <h2>
        {t(KIND_TITLE[kind])} — {employee.name}
      </h2>
      <div className={s.form}>
        {kind === "leave" && (
          <>
            <Field label="Type of leave">
              <Select value={f.type} onChange={set("type")} data-testid="hr-r-leavetype">
                {LEAVE_TYPES.map((l) => (
                  <option key={l.key} value={l.key}>
                    {t(l.label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="First day">
              <Input type="date" value={f.from} onChange={set("from")} data-testid="hr-r-from" />
            </Field>
            <Field label="Last day">
              <Input type="date" value={f.to} onChange={set("to")} data-testid="hr-r-to" />
              <Err text={show("dates")} />
            </Field>
            <p className={s.note} style={{ gridColumn: "1 / -1" }}>
              {t("{n} days.", { n: days })} {leaveDef && t(leaveDef.law)}
              {leaveDef?.days && days > leaveDef.days
                ? ` ${t("That is more than the {n} days the law gives.", { n: leaveDef.days })}`
                : ""}
            </p>
          </>
        )}
        {kind === "review" && (
          <>
            <Field label="Review">
              <Input value={f.title} onChange={set("title")} placeholder={t("e.g. Annual review 2026")} />
            </Field>
            <Field label="Date">
              <Input type="date" value={f.on} onChange={set("on")} />
            </Field>
            <Field label="Rating">
              <Select value={f.rating} onChange={set("rating")} data-testid="hr-r-rating">
                {RATINGS.map((r) => (
                  <option key={r} value={r}>
                    {t(r)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Strengths">
              <Input value={f.strengths} onChange={set("strengths")} />
            </Field>
            <Field label="Goals for next year">
              <Input value={f.goals} onChange={set("goals")} />
            </Field>
          </>
        )}
        {kind === "discipline" && (
          <>
            <Field label="What happened">
              <Input
                value={f.title}
                onChange={set("title")}
                placeholder={t("e.g. Absent without notice")}
                data-testid="hr-r-title"
              />
              <Err text={show("title")} />
            </Field>
            <Field label="Date">
              <Input type="date" value={f.on} onChange={set("on")} />
            </Field>
            <Field label="Action taken">
              <Select value={f.action} onChange={set("action")}>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(a)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Rule or article">
              <Input value={f.article} onChange={set("article")} placeholder={t("e.g. Work regulations, clause 12")} />
            </Field>
            <Field label="Notes">
              <Input value={f.text} onChange={set("text")} />
            </Field>
            <p className={s.note} style={{ gridColumn: "1 / -1" }}>
              {t(
                "Confidential: only the HR manager and super admin can read it. Tell the employee in writing and hear them first (Labor Law art. 71).",
              )}
            </p>
          </>
        )}
        {kind === "letter" && (
          <>
            <Field label="Letter">
              <Select value={f.letter} onChange={set("letter")} data-testid="hr-r-letter">
                {LETTER_TYPES.map((l) => (
                  <option key={l.key} value={l.key} disabled={l.needsPay && !can.pay}>
                    {t(l.label)}
                  </option>
                ))}
              </Select>
              <Err text={show("letter")} />
            </Field>
            <Field label="Addressed to">
              <Input
                value={f.addressee}
                onChange={set("addressee")}
                placeholder={t("optional — To whom it may concern")}
              />
            </Field>
            {(f.letter === "noc" || f.letter === "embassy") && (
              <Field label="Purpose">
                <Input
                  value={f.purpose}
                  onChange={set("purpose")}
                  placeholder={t("e.g. traveling to Jordan from 1 to 14 November")}
                />
              </Field>
            )}
          </>
        )}
        {kind === "request" && (
          <>
            <Field label="What is asked for">
              <Select value={f.type} onChange={set("type")} data-testid="hr-r-reqtype">
                {REQUEST_TYPES.filter((r) => r.kind === "request").map((r) => (
                  <option key={r.key} value={r.key}>
                    {t(r.label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="In a few words">
              <Input
                value={f.title}
                onChange={set("title")}
                placeholder={t("e.g. Move to the night shift")}
                data-testid="hr-r-title"
              />
              <Err text={show("title")} />
            </Field>
            <Field label="Details">
              <Input value={f.text} onChange={set("text")} />
            </Field>
            <p className={s.note} style={{ gridColumn: "1 / -1" }}>
              {t("The person is notified by SMS and email on receipt and on decision.")}
            </p>
          </>
        )}
        {(kind === "document" || kind === "note") && (
          <>
            <Field label={kind === "document" ? "Document" : "Note"}>
              <Input
                value={f.title}
                onChange={set("title")}
                placeholder={kind === "document" ? t("e.g. Iqama copy") : t("What to remember")}
                data-testid="hr-r-title"
              />
              <Err text={show("title")} />
            </Field>
            {kind === "document" && (
              <Field label="File (PDF or picture, up to 300 KB)">
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (!file) return;
                    const r = new FileReader();
                    r.onload = () =>
                      setF((x) => ({
                        ...x,
                        file: String(r.result),
                        fileName: file.name,
                        title: x.title || file.name.replace(/\.[^.]+$/, ""),
                      }));
                    r.readAsDataURL(file);
                  }}
                />
                <Err text={show("file")} />
              </Field>
            )}
            <Field label="Details">
              <Input value={f.text} onChange={set("text")} />
            </Field>
          </>
        )}
      </div>
      {(kind === "leave" || kind === "letter") && can.approve && (
        <label className={s.note} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={f.now}
            onChange={(e) => setF({ ...f, now: e.target.checked })}
            data-testid="hr-r-now"
          />
          {kind === "leave"
            ? t("Approve it now (you hold the approval permission)")
            : t("Issue it now (you hold the approval permission)")}
        </label>
      )}
      {add.error && <p className={s.err}>{add.error instanceof Error ? add.error.message : String(add.error)}</p>}
      <div className={s.formFoot}>
        <Button type="submit" variant="primary" disabled={add.isPending} data-testid="hr-r-save">
          {add.isPending ? t("Saving…") : t("Save")}
        </Button>
        <Button onClick={onClose}>{t("Cancel")}</Button>
      </div>
    </form>
  );
}
